import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase';

/**
 * Checks if all questionnaires are complete and sends a notification if they are.
 * Optimized for minimal client-side performance impact.
 * 
 * @param {string} userId - The patient's user ID
 * @param {string} currentFormKey - The key of the form that was just completed
 * @returns {Promise<{allComplete: boolean, alreadyNotified: boolean}>}
 */
export const checkAndNotifyAllQuestionnairesComplete = async (userId, currentFormKey) => {
    if (!userId) return { allComplete: false };

    try {
        console.log(`[NotificationFunctions] Checking questionnaire completion for user: ${userId}`);

        // Get patient data with a single Firestore read
        const patientDoc = await getDoc(doc(db, 'patients', userId));
        if (!patientDoc.exists()) return { allComplete: false };

        const patientData = patientDoc.data();

        // Skip if notification already sent
        if (patientData.allQuestionnairesCompletedEmailSent) {
            console.log('Notification already sent for all questionnaires completed');
            return { allComplete: true, alreadyNotified: true };
        }

        // Check whether the user has already submitted feedback in a
        // previous completion cycle (e.g. before a SWAP reset their forms).
        // The caller uses this to suppress the feedback modal so the user
        // isn't asked twice.
        const hasExistingFeedback = !!patientData.formFeedback;

        const formData = patientData.formData || {};
        if (Object.keys(formData).length === 0) return { allComplete: false };

        // Efficient check if all forms are complete, excluding the current form
        const allComplete = Object.entries(formData).every(([formKey, form]) => 
            formKey === currentFormKey || (form.progress?.progressPercentage || 0) === 100
        );        

        if (allComplete) {
            console.log('All questionnaires are complete, triggering notification...');
            // Fire and forget - don't await this call
            httpsCallable(functions, 'sendAllQuestionnairesCompletedEmail')()
                .then(() => console.log('All questionnaires completed notification sent'))
                .catch(err => console.error('Error sending completion notification:', err));
        } else {
            console.log('Not all questionnaires are complete');
        }

        return { allComplete, alreadyNotified: false, hasExistingFeedback };

    } catch (error) {
        console.error('Error checking questionnaire completion:', error);
        return { allComplete: false, error };
    }
};

/**
 * Checks if a patient's PHQ-9 response indicates self-harm risk and sends an urgent notification.
 * This is triggered when a patient answers question 9 with any option other than "Not at all".
 * 
 * @param {string} userId - The patient's user ID
 * @param {string} answer - The patient's answer to the self-harm question
 * @param {string} questionText - The text of the question that was answered
 * @returns {Promise<{success: boolean, alreadyNotified: boolean}>}
 */
export const checkAndNotifySelfHarmConcern = async (userId, answer, questionText) => {
    if (!userId || !answer) return { success: false };

    try {
        console.log(`[NotificationFunctions] Checking self-harm concern for user: ${userId}`);

        // These answers indicate concern
        const concerningAnswers = ["Several days", "More than half the days", "Nearly every day"];
        
        // Only proceed if the answer is concerning
        if (!concerningAnswers.includes(answer)) {
            console.log(`[NotificationFunctions] Answer "${answer}" does not indicate self-harm concern`);
            return { success: true, concernDetected: false };
        }
        
        console.log(`[NotificationFunctions] Detected concerning answer: "${answer}"`);
        
        // Get patient data with a single Firestore read to check if we've already notified
        const patientDoc = await getDoc(doc(db, 'patients', userId));
        if (!patientDoc.exists()) return { success: false };

        const patientData = patientDoc.data();
        const timestamp = new Date().toISOString();

        // Skip if we've sent a notification in the last 24 hours
        const lastNotification = patientData.lastSelfHarmNotificationSent;
        if (lastNotification) {
            const lastTime = new Date(lastNotification);
            const now = new Date();
            const hoursSinceLastNotification = (now - lastTime) / (1000 * 60 * 60);
            
            if (hoursSinceLastNotification < 24) {
                console.log('Self-harm notification already sent in the last 24 hours');
                return { success: true, alreadyNotified: true };
            }
        }

        console.log('Sending self-harm concern notification...');
        
        // Call the cloud function to send the notification
        const result = await httpsCallable(functions, 'sendSelfHarmConcernEmail')({
            patientId: userId,
            answer: answer,
            questionText: questionText,
            timestamp: timestamp
        });
        
        console.log('Self-harm concern notification sent:', result);
        return { success: true, concernDetected: true };

    } catch (error) {
        console.error('Error handling self-harm concern notification:', error);
        return { success: false, error };
    }
};